import { Currency } from "@/Apollo/schema";
import { formatPrice } from "@/common/utils/intl.utils";

/**
 * WHO-5947. Dates in reservation screens: "Thu 24 Sep, 14:30" and "Thu 24 Sep",
 * always in Europe/Amsterdam (the gateway's reservedUntilDate is an
 * Amsterdam calendar date too).
 */
const TIME_ZONE = "Europe/Amsterdam";

/** English follows the spec's "Thu 24 Sep" (day before month). */
const intlLocale = (locale: string) => (locale === "en" ? "en-GB" : locale);

const toDate = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }

  // A bare 'YYYY-MM-DD' is read as noon so no time zone shifts the day.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00Z`)
    : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatReservationDate = (
  value: string | null | undefined,
  locale = "en"
) => {
  const date = toDate(value);

  if (!date) {
    return "";
  }

  return new Intl.DateTimeFormat(intlLocale(locale), {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: TIME_ZONE,
  }).format(date);
};

export const formatReservationDateTime = (
  value: string | null | undefined,
  locale = "en"
) => {
  const date = toDate(value);

  if (!date) {
    return "";
  }

  const time = new Intl.DateTimeFormat(intlLocale(locale), {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: TIME_ZONE,
  }).format(date);

  return `${formatReservationDate(value, locale)}, ${time}`;
};

/** Whole hours left until `value`, never below zero. */
export const hoursLeft = (
  value: string | null | undefined,
  now = new Date()
) => {
  const date = toDate(value);

  if (!date) {
    return 0;
  }

  return Math.max(0, Math.floor((date.getTime() - now.getTime()) / 3_600_000));
};

/**
 * WHO-5947 F6: the site's one money formatter (`formatPrice`, which is what
 * `{{price, currency}}` in translations uses too), so "€ 1.600,-" in Dutch.
 */
export const formatReservationMoney = (
  money: { amount: number; currency: string } | null | undefined,
  locale = "en"
) => {
  if (!money) {
    return "";
  }

  return formatPrice(
    { amount: money.amount, currency: money.currency as Currency },
    { locale }
  );
};

/** Search hit `on_hold_until` (unix seconds): reserved while in the future. */
export const isOnHoldUntilActive = (
  onHoldUntil: number | null | undefined,
  now = Date.now()
) => typeof onHoldUntil === "number" && onHoldUntil * 1000 > now;

/** The public 'YYYY-MM-DD' end date is still today or later (Amsterdam). */
export const isReservedUntilDateActive = (
  reservedUntilDate: string | null | undefined
) => !!toDate(reservedUntilDate);

/**
 * WHO-5947 F11: what the reserving Pro Buyer actually pays, the lower of the
 * frozen reservation price and the current buy-now price.
 */
export const payableReservationAmount = (
  fixedPrice: { amount: number } | null | undefined,
  currentAmount: number | null | undefined
) => {
  const amounts = [fixedPrice?.amount, currentAmount].filter(
    (amount): amount is number => typeof amount === "number" && amount > 0
  );

  return amounts.length > 0 ? Math.min(...amounts) : null;
};

/**
 * The product as analytics should see it for the holder: same item, buy-now
 * amount replaced by the payable reservation price, so add_to_cart `value`
 * is e.g. 1600 and not the list price 2000.
 */
export const withReservationPrice = <
  TProduct extends {
    auction?: { buy_now_price?: { amount?: number | null } | null } | null;
  },
>(
  product: TProduct,
  fixedPrice: { amount: number } | null | undefined
): TProduct => {
  const payable = payableReservationAmount(
    fixedPrice,
    product.auction?.buy_now_price?.amount
  );

  if (payable === null || !product.auction?.buy_now_price) {
    return product;
  }

  return {
    ...product,
    auction: {
      ...product.auction,
      buy_now_price: { ...product.auction.buy_now_price, amount: payable },
    },
  };
};
